/**
 * npm i pako
 * npm i @types/pako
 *
 */

import { AudioConfig } from './AudioConfig';

interface AudioChunk {
  audioBuffer: AudioBuffer;
  duration: number;
  bsArray: [][];
}

export default class AudioPlayer extends EventTarget {
  // 事件类型常量
  static readonly BLEND_SHAPE_EVENT = 'blendShapeEvent';
  static readonly PLAY_END_EVENT = 'playEndEvent';  ///播放结束事件

  private audioContext: AudioContext;
  private source: AudioBufferSourceNode | undefined;
  private gainNode: GainNode;
  private audioChunks: AudioChunk[];
  public playing = false;
  public muted = false;
  public bs: number[] = [];
  private config: AudioConfig;
  private playToken = 0;
  private readonly unlockAudioContext: () => void;

  constructor(config?: AudioConfig) {
    super();

    // 使用传入的配置或创建默认配置
    this.config = config || new AudioConfig();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    this.audioContext = new AC();
    this.audioChunks = [];

    // 创建 GainNode 用于控制音量（静音功能）
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.gainNode = this.audioContext.createGain?.() || (this.audioContext as any).createGainNode();
    this.gainNode.connect(this.audioContext.destination);
    this.gainNode.gain.value = 1;

    // 移动端浏览器：首次用户交互时解锁音频上下文
    this.unlockAudioContext = () => {
      void this.audioContext.resume();
      this.gainNode.gain.value = this.muted ? 0 : 1;
    };
    document.addEventListener('touchstart', this.unlockAudioContext, { once: true });
    document.addEventListener('click', this.unlockAudioContext, { once: true });
  }

  public addAudio(audioStr: string, bsArray: [][]) {
    // console.log('add audio chunk  playing:', this.playing);
    const { audioBuffer, duration } = this.createAudioBufferFromInt16Base64(audioStr);
    this.audioChunks.push({ audioBuffer, duration, bsArray });

    if (!this.playing) {
      this.playNextAudioChunk();
      this.playing = true;
    }
  }

  /**
   * 设置静音状态
   * @param muted true 为静音，false 为正常音量
   * @param smooth 是否使用平滑过渡（默认 true）
   */
  public setMute(muted: boolean, smooth = true) {
    this.muted = muted;
    const targetVolume = muted ? 0 : 1;
    const currentTime = this.audioContext.currentTime;

    // 平滑过渡避免爆音
    if (smooth && this.gainNode.gain.setTargetAtTime) {
      this.gainNode.gain.setTargetAtTime(targetVolume, currentTime, 0.015);
    } else {
      this.gainNode.gain.value = targetVolume;
    }
  }

  /// 用于打断播放
  public stop() {
    // console.log('stop audio player');
    this.playToken++;
    if (this.source) {
      try {
        this.source.stop();
      } catch {
      }
      try {
        this.source.disconnect();
      } catch {
      }
      this.source = undefined;
    }
    this.audioChunks = [];
    this.playing = false;
    this.bs = [];
  }

  public dispose(): void {
    this.stop();
    try {
      this.gainNode?.disconnect();
    } catch { }
    document.removeEventListener('touchstart', this.unlockAudioContext);
    document.removeEventListener('click', this.unlockAudioContext);
    if (this.audioContext?.state !== 'closed') {
      void this.audioContext.close().catch(() => undefined);
    }
  }

  private async playNextAudioChunk() {
    // AudioContext状态
    // console.log('AudioContext ', this.audioContext.state);
    if (!this.playing && this.audioChunks.length === 0) {
      return;
    }

    const chunk = this.audioChunks.shift();
    if (!chunk) {
      this.playing = false;
      this.bs = [];
      console.log('play end');
      this.dispatchEvent(new CustomEvent(AudioPlayer.PLAY_END_EVENT));
      return;
    }

    const { audioBuffer, duration, bsArray } = chunk;
    const token = this.playToken;

    // 确保在播放前恢复到运行态（移动端常为 suspended/interrupted）
    if (this.audioContext.state !== 'running') {
      await this.audioContext.resume();
    }

    // 创建音频源并连接到 gainNode（通过 gainNode 控制静音）
    this.source = this.audioContext.createBufferSource();
    this.source.buffer = audioBuffer;
    this.source.connect(this.gainNode); // 连接到 gainNode 而不是直接连接到 destination
    this.source.addEventListener('ended', () => this.playNextAudioChunk());
    this.source.start(0);

    const startTime = this.audioContext.currentTime;
    let lastBsId = -1; // 添加变量来跟踪上一次的bsId

    const updateProgress = () => {
      // 静音时不发送表情数据
      if (this.muted) return;
      if (token !== this.playToken) return;

      const currentTime = this.audioContext.currentTime - startTime;
      const progress = currentTime / duration;
      const bsId = Math.floor(progress * (bsArray.length - 1));

      // 只有当bsId发生变化时才更新bs并发送事件
      if (bsId !== lastBsId) {
        lastBsId = bsId;
        this.bs = bsArray[bsId];

        this.dispatchEvent(
          new CustomEvent(AudioPlayer.BLEND_SHAPE_EVENT, {
            detail: { bs: this.bs },
          })
        );
      }

      if (currentTime < duration) {
        requestAnimationFrame(updateProgress);
      } else {
        // 播放结束时，如果bs不为空数组，则发送最后一次事件
        if (this.bs?.length > 0) {
          this.bs = [];
          this.dispatchEvent(
            new CustomEvent(AudioPlayer.BLEND_SHAPE_EVENT, {
              detail: { bs: this.bs },
            })
          );
        }
      }
    };
    requestAnimationFrame(updateProgress);
  }

  private createAudioBufferFromInt16Base64(base64: string): {
    audioBuffer: AudioBuffer;
    duration: number;
  } {
    /// 将 Base64 编码的压缩数据转换为 Uint8Array
    const binaryString = atob(base64);
    const compressedData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      compressedData[i] = binaryString.charCodeAt(i);
    }

    // /// 使用 pako 解压缩数据
    // const decompressedData = pako.inflate(compressedData);

    /// 将解压缩后的数据转换为 Int16Array
    const int16Array = new Int16Array(compressedData.buffer);

    //// 创建 AudioBuffer
    const numChannels = this.config.numChannels;
    const sampleRate = this.config.playerSampleRate;
    const frameCount = int16Array.length;
    const audioBuffer = this.audioContext.createBuffer(numChannels, frameCount, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = int16Array[i] / 32768; // 将 Int16 转换为 -1 到 1 之间的浮点数
    }

    const duration = frameCount / sampleRate;
    return { audioBuffer, duration };
  }
}
